/**
 * The compact Contact Us band that closes the Home screen.
 *
 * It replaces the impact statistics on Home, which had grown into the tallest
 * thing on the page and pushed the hero off small phones. The statistics are
 * not deleted — they still open every other screen, and Impact shows them in
 * full — but Home's job is to get someone donating or talking to a human, and
 * a row of numbers does neither.
 *
 * Every channel is server-configured. One that an operator has left blank is
 * not rendered at all rather than shown as a dead button, and if none are
 * configured the band disappears instead of occupying space to say nothing.
 */

import { memo, useCallback } from 'react';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import type { SupportContact } from '@mfa/domain';
import { breakpoints, colors, radius, sizing, space } from '../../theme';
import { openDialler, openMail, openWebsite, openWhatsApp } from '../../core/linking/contact';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface ContactBarProps {
  support?: SupportContact;
  testID?: string;
}

interface Channel {
  key: string;
  icon: string;
  label: string;
  hint: string;
  open: () => void;
}

/** Below this the four labels wrap to a second row and cost real height. */
const NARROW_VIEWPORT = 360;

/**
 * From a tablet's width up — and on the website — there is height to spare,
 * so the band grows to full-size controls: 44pt targets without a hit slop,
 * larger icons and labels. Phones keep the short band.
 */
const ROOMY_VIEWPORT = breakpoints.md;

/**
 * Grows each control's touch area back towards the 44pt minimum.
 *
 * The band paints at roughly half that. On iOS and Android this restores the
 * target; react-native-web ignores hitSlop, so the web build's target is the
 * painted 24pt — a known consequence of the height this bar is asked to be,
 * recorded here rather than left to be discovered. Padding the row to 44pt
 * instead would undo the height; a negative margin would make the box overlap
 * the pinned action above it and steal taps from it.
 */
const VISIBLE_ROW = 24;
const HIT_SLOP = {
  top: (sizing.touchTarget - VISIBLE_ROW) / 2,
  bottom: (sizing.touchTarget - VISIBLE_ROW) / 2,
  left: space.sm,
  right: space.sm,
};

export const ContactBar = memo(function ContactBar({
  support,
  testID = 'contact-bar',
}: ContactBarProps) {
  const { width, height } = useWindowDimensions();
  // On a narrow phone the band drops its labels and keeps the icons: four
  // channels then sit on one row instead of two, and the height that saves is
  // height the screen above it needs. Each control still carries its own
  // accessibility label, so nothing is lost to a screen reader.
  const iconOnly = width <= NARROW_VIEWPORT;
  // Not on a phone turned on its side: wide, but with no height to spare.
  const roomy = width >= ROOMY_VIEWPORT && height >= sizing.shortWindowHeight + space['6xl'];
  const whatsapp = support?.whatsapp?.trim();
  const phone = support?.phone?.trim();
  const email = support?.email?.trim();
  const website = support?.website?.trim();

  const onWhatsApp = useCallback(() => {
    if (whatsapp) void openWhatsApp(whatsapp, 'Hello, I have a question about my order.');
  }, [whatsapp]);
  const onCall = useCallback(() => {
    if (phone) void openDialler(phone);
  }, [phone]);
  const onEmail = useCallback(() => {
    if (email) void openMail(email, 'Meal4ALL enquiry');
  }, [email]);
  const onWebsite = useCallback(() => {
    if (website) void openWebsite(website);
  }, [website]);

  const t = useT();

  const channels: Channel[] = [];
  if (whatsapp) {
    channels.push({
      key: 'whatsapp',
      icon: 'chat',
      label: t('WhatsApp'),
      hint: `Message us on WhatsApp at ${whatsapp}`,
      open: onWhatsApp,
    });
  }
  if (email) {
    channels.push({
      key: 'email',
      icon: 'email',
      label: t('Email'),
      hint: `Email us at ${email}`,
      open: onEmail,
    });
  }
  if (phone) {
    channels.push({
      key: 'call',
      icon: 'phone',
      label: t('Call'),
      hint: `Call us on ${phone}`,
      open: onCall,
    });
  }
  if (website) {
    channels.push({
      key: 'website',
      icon: 'globe',
      label: t('Website'),
      hint: `Open ${website}`,
      open: onWebsite,
    });
  }

  // The band closes the screen, so it sits on the system's bottom edge: the
  // home indicator on iOS, and on Android 15+ — which draws every app edge to
  // edge — the gesture or button bar. Its green runs on under that bar; its
  // controls stay above it.
  const { bottom: bottomInset } = useSafeAreaInsets();
  if (channels.length === 0) return bottomInset ? <View style={{ height: bottomInset }} /> : null;

  const band = [styles.band, roomy && styles.bandRoomy];
  const inset = { paddingBottom: (roomy ? space.md : space.xxs) + bottomInset };

  return (
    <View style={bottomInset > 0 ? [...band, inset] : band} testID={testID}>
      <View style={styles.channels}>
        {channels.map((channel) => (
          <Pressable
            key={channel.key}
            onPress={channel.open}
            accessibilityRole="button"
            accessibilityLabel={channel.label}
            accessibilityHint={channel.hint}
            testID={`contact-${channel.key}`}
            // The band is deliberately short, so the tappable area is grown
            // back past the visible one rather than the row being padded to
            // 44pt. The target still clears the minimum; only the paint is
            // smaller.
            hitSlop={roomy ? undefined : HIT_SLOP}
            style={({ pressed }) => [
              styles.channel,
              roomy && styles.channelRoomy,
              pressed && styles.pressed,
            ]}
          >
            {roomy ? (
              // A light disc behind each icon, so the dark receiver of the
              // call icon reads on the green as well as the others.
              <View style={styles.iconDisc}>
                <Glyph name={channel.icon} size={sizing.icon.md} />
              </View>
            ) : (
              <Glyph name={channel.icon} size={sizing.icon.sm} />
            )}
            {iconOnly ? null : (
              <Text variant={roomy ? 'bodyStrong' : 'footerCaption'} tone="onBrand">
                {channel.label}
              </Text>
            )}
          </Pressable>
        ))}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  band: {
    // Full width and deliberately short: this closes every screen without
    // becoming the reason one scrolls. No heading either — four labelled
    // controls on a green band do not need to be told they are contacts, and
    // the line cost more height than it earned.
    width: '100%',
    backgroundColor: colors.bg.brand,
    paddingVertical: space.xxs,
    paddingHorizontal: space.xs,
  },
  bandRoomy: {
    paddingVertical: space.md,
    paddingHorizontal: space.lg,
  },
  channels: {
    flexDirection: 'row',
    // Evenly spaced rather than centred with fixed gaps, so the margin at each
    // end matches the space between the channels at any width.
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  channel: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.xs,
    minHeight: VISIBLE_ROW,
    paddingVertical: space.xxs,
    paddingHorizontal: space.sm,
    borderRadius: radius.sm,
  },
  iconDisc: {
    width: sizing.touchTarget - space.sm,
    height: sizing.touchTarget - space.sm,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.bg.surface,
  },
  channelRoomy: {
    gap: space.sm,
    minHeight: sizing.touchTarget,
    paddingHorizontal: space.lg,
  },
  pressed: {
    opacity: 0.7,
  },
});
